class DulinSpinGallery extends HTMLElement {
  connectedCallback() {
    if (!document.getElementById('dulin-fired-ceramic-tones')) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.position = 'absolute';
      svg.style.pointerEvents = 'none';
      svg.innerHTML = '<defs><filter id="dulin-fired-ceramic-tones" color-interpolation-filters="sRGB"><feComponentTransfer><feFuncR type="gamma" amplitude="1" exponent="0.94" offset="0"/><feFuncG type="gamma" amplitude="1" exponent="0.94" offset="0"/><feFuncB type="gamma" amplitude="1" exponent="0.94" offset="0"/></feComponentTransfer><feColorMatrix type="saturate" values="0.80"/></filter></defs>';
      document.body.append(svg);
    }
    this.controller?.abort();
    this.controller = new AbortController();
    this.color = this.dataset.initialColor || 'rouge';
    this.frame = 0;
    this.mode = 'photo';
    this.images = new Map();
    this.stage = this.querySelector('[data-spin-stage]');
    this.photo = this.querySelector('[data-spin-photo]');
    this.canvas = this.querySelector('canvas');
    this.context = this.canvas.getContext('2d');
    this.slider = this.querySelector('[data-spin-range]');
    this.toggle = this.querySelector('[data-spin-toggle]');
    this.play = this.querySelector('[data-spin-play]');
    this.labels = JSON.parse(this.querySelector('[data-spin-labels]').textContent);
    this.data = JSON.parse(this.querySelector('[data-spin-data]').textContent);
    const productPhotos = document.querySelector('[data-dulin-product-photos="' + this.dataset.productId + '"]');
    if (productPhotos) {
      const photos = JSON.parse(productPhotos.textContent);
      const thumbContainer = this.querySelector('.dulin-spin-thumbs');
      const groups = {};
      for (const photo of photos) {
        const color = String(photo.alt || '').toLowerCase().match(/dessous (bleu|rose|rouge)/)?.[1];
        if (color && this.data.colors[color]) (groups[color] ||= []).push(photo);
      }
      if (Object.keys(this.data.colors).every(color => groups[color]?.length)) {
        thumbContainer.replaceChildren();
        this.photo.style.filter = 'none';
        for (const color of ['bleu', 'rose', 'rouge']) {
          if (!groups[color]) continue;
          this.data.colors[color].faces = groups[color].map(photo => photo.src);
          groups[color].forEach((photo, face) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'dulin-spin-thumb';
            button.dataset.spinFace = String(face);
            button.dataset.color = color;
            button.setAttribute('aria-label', this.data.colors[color].label + ', ' + this.labels.face + ' ' + (face + 1));
            button.setAttribute('aria-pressed', 'false');
            const img = document.createElement('img');
            img.src = photo.thumb;
            img.alt = '';
            img.width = 180;
            img.height = 180;
            img.loading = 'lazy';
            img.style.filter = 'none';
            const dot = document.createElement('span');
            dot.className = 'dulin-spin-dot';
            dot.setAttribute('aria-hidden', 'true');
            dot.style.setProperty('--spin-color', {bleu:'#2556bc',rose:'#d5608b',rouge:'#a62a46'}[color]);
            button.append(img, dot);
            thumbContainer.append(button);
          });
        }
      }
    }
    if (!this.data.colors[this.color]) this.color = 'rouge';
    const options = { signal: this.controller.signal };
    this.querySelectorAll('[data-spin-face]').forEach(button => {
      button.addEventListener('click', () => {
        this.chooseVariant(button.dataset.color);
        this.showPhoto(button.dataset.color, Number(button.dataset.spinFace));
      }, options);
    });
    document.addEventListener('change', event => {
      const picker = event.target.closest('variant-picker');
      if (!picker || picker.dataset.productId !== this.dataset.productId) return;
      const next = String(event.target.value || '').toLowerCase();
      if (!this.data.colors[next] || next === this.color) return;
      const wasSpin = this.mode === 'spin';
      this.showPhoto(next, 0);
      if (wasSpin) this.showSpin();
    }, options);
    this.toggle.addEventListener('click', () => this.mode === 'spin' ? this.showPhoto(this.color, 0) : this.showSpin(), options);
    this.play.addEventListener('click', () => {
      if (this.timer) return this.stop();
      this.play.setAttribute('aria-pressed', 'true');
      this.play.setAttribute('aria-label', this.labels.pause);
      this.play.textContent = 'Ⅱ';
      this.timer = setInterval(() => this.draw(this.frame + 1), 110);
    }, options);
    this.slider.addEventListener('input', () => { this.stop(); this.draw(Number(this.slider.value)); }, options);
    this.stage.addEventListener('pointerdown', event => {
      if (this.mode !== 'spin') return;
      this.stop();
      this.pointer = { id: event.pointerId, x: event.clientX, frame: this.frame };
      this.stage.setPointerCapture(event.pointerId);
    }, options);
    this.stage.addEventListener('pointermove', event => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      this.draw(this.pointer.frame + Math.round((event.clientX - this.pointer.x) / 7));
    }, options);
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      this.stage.addEventListener(name, () => this.pointer = null, options);
    }
    this.stage.addEventListener('keydown', event => {
      if (this.mode !== 'spin' || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); this.stop(); this.draw(this.frame + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
    this.showPhoto(this.color, 0);
  }
  disconnectedCallback() { this.stop(); this.controller?.abort(); }
  chooseVariant(color) {
    const pickers = document.querySelectorAll(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    for (const picker of pickers) {
      const input = [...picker.querySelectorAll('input[type="radio"]')].find(el => el.value.toLowerCase() === color);
      if (input) { if (!input.checked) input.click(); return; }
      const select = picker.querySelector('select');
      const option = select && [...select.options].find(el => el.value.toLowerCase() === color);
      if (option) { select.value = option.value; select.dispatchEvent(new Event('change', { bubbles: true })); return; }
    }
  }
  showPhoto(color, face) {
    this.stop();
    if (this.color !== color) { this.images.clear(); this.sprites = null; }
    this.request = (this.request || 0) + 1;
    this.color = color;
    this.mode = 'photo';
    this.photo.src = this.data.colors[color].faces[face];
    this.photo.alt = `${this.data.title} — ${this.data.colors[color].label}, ${this.labels.face} ${face + 1}`;
    this.photo.hidden = false;
    this.canvas.hidden = true;
    this.querySelector('[data-spin-controls]').hidden = true;
    this.querySelector('[data-spin-hint]').hidden = true;
    this.stage.classList.remove('is-spinning');
    this.stage.removeAttribute('aria-busy');
    this.toggle.setAttribute('aria-pressed', 'false');
    this.toggle.textContent = this.labels.view;
    this.querySelector('[data-spin-status]').textContent = '';
    this.querySelectorAll('[data-spin-face]').forEach(button => button.setAttribute('aria-pressed', button.dataset.color === color && Number(button.dataset.spinFace) === face ? 'true' : 'false'));
  }
  async loadSprites(color) {
    if (!this.images.has(color)) {
      const promise = Promise.all(this.data.colors[color].sprites.map(src => new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      }))).catch(error => { this.images.delete(color); throw error; });
      this.images.set(color, promise);
    }
    return this.images.get(color);
  }
  async showSpin() {
    this.stop();
    const color = this.color, request = ++this.request;
    this.toggle.textContent = this.labels.loading;
    this.stage.setAttribute('aria-busy', 'true');
    try {
      const images = await this.loadSprites(color);
      if (!this.isConnected || request !== this.request || color !== this.color) return;
      this.sprites = images;
      this.mode = 'spin';
      this.photo.hidden = true;
      this.canvas.hidden = false;
      this.querySelector('[data-spin-controls]').hidden = false;
      this.querySelector('[data-spin-hint]').hidden = false;
      this.stage.classList.add('is-spinning');
      this.toggle.textContent = this.labels.photos;
      this.toggle.setAttribute('aria-pressed', 'true');
      this.querySelectorAll('[data-spin-face]').forEach(button => button.setAttribute('aria-pressed', 'false'));
      this.draw(this.data.colors[color].startFrame || 0);
    } catch {
      if (request === this.request) {
        this.toggle.textContent = this.labels.view;
        this.querySelector('[data-spin-status]').textContent = this.labels.error;
      }
    } finally { if (request === this.request) this.stage.removeAttribute('aria-busy'); }
  }
  draw(frame) {
    if (this.mode !== 'spin') return;
    const count = this.data.frames || 72;
    const perSprite = this.data.spriteFrames || 36;
    const columns = this.data.columns || 6;
    this.frame = (frame % count + count) % count;
    const local = this.frame % perSprite;
    const tile = this.data.tile;
    this.context.drawImage(this.sprites[Math.floor(this.frame / perSprite)], local % columns * tile, Math.floor(local / columns) * tile, tile, tile, 0, 0, 600, 600);
    this.slider.max = String(count - 1);
    this.slider.value = String(this.frame);
    const angle = Math.round(this.frame * 360 / count);
    this.slider.setAttribute('aria-valuetext', angle + '°');
    this.querySelector('[data-spin-angle]').textContent = angle + '°';
  }
  stop() {
    clearInterval(this.timer); this.timer = null;
    if (this.play) { this.play.textContent = '▶'; this.play.setAttribute('aria-pressed', 'false'); this.play.setAttribute('aria-label', this.labels.play); }
  }
}
if (!customElements.get('dulin-spin-gallery')) customElements.define('dulin-spin-gallery', DulinSpinGallery);

